/**
 * Types and option arrays for the Buyback / No Cost EMI capture flow.
 *
 * The flow is a small decision tree:
 *
 *     Q1 eligibility (yes / no)
 *       └─ yes → Q2 customer_path (Hardware Services / EMI / Buyback)
 *             └─ emi-buyback → Q3 emi_or_buyback (EMI / Buyback)
 *                   └─ buyback → Q4 buyback_opted (yes / no)
 *                         ├─ yes → quotation_amount
 *                         └─ no  → rejection_reason
 */

export type EligibilityYesNo = "" | "yes" | "no";
export type CustomerPath = "" | "hardware" | "emi-buyback";
export type EmiOrBuyback = "" | "emi" | "buyback";
export type BuybackOpted = "" | "yes" | "no";
export type RejectionReason =
  | ""
  | "pricing-issue"
  | "asking-cash-or-same-time-refund"
  | "decide-later"
  | "price-error";

/** Server shape returned by the ``buyback_emi_records`` endpoint. */
export interface BuybackEMIRecord {
  id?: number;
  url?: string;
  ticket: string;
  guid?: string;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  notes?: string;

  eligibility_yes_no: Exclude<EligibilityYesNo, "">;
  customer_path?: CustomerPath | null;
  emi_or_buyback?: EmiOrBuyback | null;
  buyback_opted?: BuybackOpted | null;
  quotation_amount?: string | null;
  quotation_id?: string | null;
  rejection_reason?: RejectionReason | null;

  eligibility_yes_no_label?: string;
  customer_path_label?: string;
  emi_or_buyback_label?: string;
  buyback_opted_label?: string;
  rejection_reason_label?: string;
}

/** Payload accepted by ``useBuybackEMI.save`` (already in API field names). */
export interface SaveBuybackEMIRecordPayload {
  eligibility_yes_no: Exclude<EligibilityYesNo, "">;
  customer_path?: CustomerPath | null;
  emi_or_buyback?: EmiOrBuyback | null;
  buyback_opted?: BuybackOpted | null;
  quotation_amount?: string | null;
  quotation_id?: string | null;
  rejection_reason?: RejectionReason | null;
  notes?: string;
}

export interface BuybackEMIFormValues {
  /** Q1 — eligibility yes/no (always asked). */
  eligibilityYesNo: EligibilityYesNo;
  /** Q2 — only when Q1 = "yes". */
  path: CustomerPath;
  /** Q3 — only when ``path === "emi-buyback"``. */
  emiOrBuyback: EmiOrBuyback;
  /** Q4 — only when ``emiOrBuyback === "buyback"``. */
  buybackOpted: BuybackOpted;
  /** Captured when ``buybackOpted === "yes"``. */
  quotationAmount: string;
  /** Captured whenever ``buybackOpted`` is answered (yes or no). */
  quotationId: string;
  /** Captured when ``buybackOpted === "no"``. */
  rejectionReason: RejectionReason;
  /** Free-text remarks (maps to ``notes``); required. */
  remarks: string;
}

/** Q2 picker (only shown when Q1 = "yes"). */
export const PATH_OPTIONS: ReadonlyArray<{
  value: Exclude<CustomerPath, "">;
  label: string;
}> = [
  { value: "hardware", label: "Proceeded for Hardware Services" },
  { value: "emi-buyback", label: "Proceeded for No Cost EMI / Buyback" },
];

/** Q3 picker (only shown when path = "emi-buyback"). */
export const EMI_OR_BUYBACK_OPTIONS: ReadonlyArray<{
  value: Exclude<EmiOrBuyback, "">;
  label: string;
}> = [
  { value: "emi", label: "Customer Proceed for No Cost EMI" },
  { value: "buyback", label: "Proceeded for Buyback" },
];

/** Q4 reasons (only shown when buybackOpted = "no"). */
export const REJECTION_REASON_OPTIONS: ReadonlyArray<{
  value: Exclude<RejectionReason, "">;
  label: string;
}> = [
  { value: "pricing-issue", label: "Pricing Issue" },
  {
    value: "asking-cash-or-same-time-refund",
    label: "Asking for Cash or Same Time Refund",
  },
  { value: "decide-later", label: "Decide Later" },
  { value: "price-error", label: "Price Error or Issues" },
];

export const EMPTY_BUYBACK_FORM_VALUES: BuybackEMIFormValues = {
  eligibilityYesNo: "",
  path: "",
  emiOrBuyback: "",
  buybackOpted: "",
  quotationAmount: "",
  quotationId: "",
  rejectionReason: "",
  remarks: "",
};

/**
 * Normalize an enum-ish value from the API (which may be ``""``, ``null``, or
 * a real choice value) down to the closed form union used by the form.
 */
function normalizeChoice<T extends string>(
  value: unknown,
  allowed: ReadonlyArray<T>,
): "" | T {
  if (typeof value !== "string") return "";
  return (allowed as ReadonlyArray<string>).includes(value) ? (value as T) : "";
}

const ELIGIBILITY_VALUES: ReadonlyArray<Exclude<EligibilityYesNo, "">> = [
  "yes",
  "no",
];
const PATH_VALUES: ReadonlyArray<Exclude<CustomerPath, "">> = [
  "hardware",
  "emi-buyback",
];
const EMI_OR_BUYBACK_VALUES: ReadonlyArray<Exclude<EmiOrBuyback, "">> = [
  "emi",
  "buyback",
];
const BUYBACK_OPTED_VALUES: ReadonlyArray<Exclude<BuybackOpted, "">> = [
  "yes",
  "no",
];
const REJECTION_REASON_VALUES: ReadonlyArray<Exclude<RejectionReason, "">> = [
  "pricing-issue",
  "asking-cash-or-same-time-refund",
  "decide-later",
  "price-error",
];

/** Hydrate the form from a server record (or empty defaults for ``null``). */
export function buybackRecordToFormValues(
  record: BuybackEMIRecord | null,
): BuybackEMIFormValues {
  if (!record) return EMPTY_BUYBACK_FORM_VALUES;
  return {
    eligibilityYesNo: normalizeChoice(
      record.eligibility_yes_no,
      ELIGIBILITY_VALUES,
    ),
    path: normalizeChoice(record.customer_path, PATH_VALUES),
    emiOrBuyback: normalizeChoice(
      record.emi_or_buyback,
      EMI_OR_BUYBACK_VALUES,
    ),
    buybackOpted: normalizeChoice(record.buyback_opted, BUYBACK_OPTED_VALUES),
    quotationAmount: record.quotation_amount ?? "",
    quotationId: record.quotation_id ?? "",
    rejectionReason: normalizeChoice(
      record.rejection_reason,
      REJECTION_REASON_VALUES,
    ),
    remarks: record.notes ?? "",
  };
}

/**
 * Build the API payload from current form values.
 *
 * Empty-string selections become ``null`` so the backend's branch-validation
 * sees a cleared field rather than a junk choice. ``quotation_amount`` is
 * captured whenever ``buyback_opted`` is answered (YES or NO) and elided
 * otherwise.
 */
export function buybackFormValuesToPayload(
  values: BuybackEMIFormValues,
): SaveBuybackEMIRecordPayload {
  const eligibility = values.eligibilityYesNo;
  if (eligibility === "") {
    throw new Error("eligibilityYesNo is required to build a payload");
  }
  return {
    eligibility_yes_no: eligibility,
    customer_path: values.path === "" ? null : values.path,
    emi_or_buyback: values.emiOrBuyback === "" ? null : values.emiOrBuyback,
    buyback_opted: values.buybackOpted === "" ? null : values.buybackOpted,
    quotation_amount:
      (values.buybackOpted === "yes" || values.buybackOpted === "no") &&
      values.quotationAmount !== ""
        ? values.quotationAmount
        : null,
    quotation_id:
      (values.buybackOpted === "yes" || values.buybackOpted === "no") &&
      values.quotationId.trim() !== ""
        ? values.quotationId.trim()
        : null,
    rejection_reason:
      values.rejectionReason === "" ? null : values.rejectionReason,
    notes: values.remarks.trim(),
  };
}
